import { FC, useContext, useEffect, useSyncExternalStore } from 'react';
import { MoContext } from 'contexts/MoContext';
import { MyOrder } from 'ts/shared/interfaces/MOResponse';
import {
  OrdersAction,
  consumeOrdersActions,
  getOrdersActionsVersion,
  patchOrdersAction,
  peekOrdersActions,
  subscribeOrdersActions,
} from './ordersActions';

interface OrdersActiveBridgeProps {
  orders: MyOrder[];
  activeOrder?: MyOrder;
  setActiveOrder: (order: MyOrder) => void;
}

const sameOrder = (left: MyOrder | undefined, right: MyOrder) =>
  !!left &&
  left.fullSegment.orderSegId === right.fullSegment.orderSegId &&
  left.orderRow.orderId === right.orderRow.orderId;

const segmentIdsOf = (order: MyOrder) =>
  [order.orderRow.segmentId, order.fullSegment.orderSegId].map(Number);

const orderIdsOf = (order: MyOrder) =>
  [order.orderRow.orderId, order.fullSegment.orderId].map(Number);

const orderMatchesId = (order: MyOrder, id: number) =>
  segmentIdsOf(order).includes(id) || orderIdsOf(order).includes(id);

const OrdersActiveBridge: FC<OrdersActiveBridgeProps> = ({ orders, activeOrder, setActiveOrder }) => {
  const version = useSyncExternalStore(subscribeOrdersActions, getOrdersActionsVersion);
  const { visibleRows } = useContext(MoContext);

  useEffect(() => {
    const firstVisible = (list: MyOrder[]) => {
      if (!list.length) return undefined;
      if (visibleRows.length) {
        for (const segmentId of visibleRows) {
          const found = list.find((order) => segmentIdsOf(order).includes(Number(segmentId)));
          if (found) return found;
        }
      }
      return list[0];
    };

    const orderAt = (index: number) => {
      if (visibleRows.length) {
        const segmentId = visibleRows[index - 1];
        return orders.find((order) => segmentIdsOf(order).includes(Number(segmentId)));
      }
      return orders[index - 1];
    };

    const orderById = (id: number) => firstVisible(orders.filter((order) => orderMatchesId(order, id)));

    const unresolved = (action: OrdersAction) => !action.segmentId && !action.orderId;

    peekOrdersActions().forEach((action) => {
      if (!unresolved(action)) return;
      const order = action.index
        ? orderAt(action.index)
        : action.targetId
          ? orderById(Number(action.targetId))
          : undefined;
      if (!order) return;
      patchOrdersAction(action, {
        ...action,
        segmentId: order.orderRow.segmentId,
        orderId: order.orderRow.orderId,
        index: undefined,
        targetId: undefined,
      });
    });

    const missed = peekOrdersActions().filter((action) => {
      if (action.action !== 'select' || !unresolved(action)) return false;
      if (action.index) {
        if (visibleRows.length) return action.index > visibleRows.length;
        return orders.length > 0 && action.index > orders.length;
      }
      return !!action.targetId && orders.length > 0 && !orderById(Number(action.targetId));
    });
    if (missed.length) consumeOrdersActions((action) => missed.includes(action));

    const select = peekOrdersActions().find(
      (action) => action.action === 'select' && (!!action.segmentId || !!action.orderId || !!action.targetId),
    );
    const targeted =
      select ||
      peekOrdersActions().find((action) => action.segmentId || action.orderId || action.targetId);
    if (!targeted) return;

    const ids = [targeted.segmentId, targeted.orderId, targeted.targetId]
      .filter((id): id is number => id != null)
      .map(Number);

    const match = firstVisible(
      orders.filter((order) => ids.some((id) => orderMatchesId(order, id))),
    );
    if (match && !sameOrder(activeOrder, match)) {
      setActiveOrder(match);
    }

    if (select && match) {
      consumeOrdersActions((action) => action === select);
    }
  }, [version, orders, activeOrder, setActiveOrder, visibleRows]);

  return null;
};

export default OrdersActiveBridge;
